import { purchaseOrderApi } from "./axios";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  PurchaseOrderI,
  DataI,
  GetPurchaseOrderParamsI,
  PurchaseOrderReceiveI,
} from "../../interfaces";
import { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";

const getPurchaseOrders = async ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
  branchId,
  supplierId,
}: GetPurchaseOrderParamsI): Promise<
  ServiceResponseI<DataI<PurchaseOrderI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (clientId) params.append("clientId", clientId.toString());
    if (branchId) params.append("branchId", branchId.toString());
    if (supplierId) params.append("supplierId", supplierId.toString());
    if (status !== undefined) params.append("status", String(status));

    const { data } = await purchaseOrderApi.get<
      ServiceResponseI<DataI<PurchaseOrderI>>
    >("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const purchaseOrderPost = async <T>(
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await purchaseOrderApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const purchaseOrderPut = async <T>(
  id: string,
  payload: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await purchaseOrderApi.put<ServiceResponseI<string>>(
      `/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const purchaseOrderById = async (
  id: string,
): Promise<ServiceResponseI<PurchaseOrderI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await purchaseOrderApi.get<
      ServiceResponseI<PurchaseOrderI>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const purchaseOrderDelete = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await purchaseOrderApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const purchaseOrderReceive = async (
  id: string,
  payload: PurchaseOrderReceiveI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await purchaseOrderApi.put<ServiceResponseI<string>>(
      `/receive/${id}`,
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const usePurchaseOrders = ({
  pagination,
  sorting,
  globalFilter,
  clientId,
  status,
  branchId,
  supplierId,
}: GetPurchaseOrderParamsI) => {
  const purchaseOrdersQuery = useQuery(
    [
      "purchase-orders",
      {
        pagination,
        sorting,
        globalFilter,
        clientId,
        status,
        branchId,
        supplierId,
      },
    ],
    () =>
      getPurchaseOrders({
        pagination,
        sorting,
        globalFilter,
        clientId,
        status,
        branchId,
        supplierId,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return { purchaseOrdersQuery };
};
